// hooks
import { useEffect, useRef } from 'react';

// types
import type { AutomaticRosteringUnavailableReason } from 'generated';
import type { AutomaticRosteringState } from '../automaticRostering.types';

// utils
import { queries } from 'RestAPIs/rapini';

type UseArQuestionSuggestionsProps = {
  organizationalUnitCode: string | null;
  startDate: string;
  endDate: string;
  enabled: boolean;
  /** The state of the calculation on screen; the questions are read again whenever it changes. */
  runState: AutomaticRosteringState;
};

/**
 * The questions the chat opens with, for the week on screen.
 *
 * <p>They come from the same frozen data the answers come from, so a suggested question can always
 * be answered — and no language model is called to produce them. A week with nothing to explain
 * gives an empty list plus the reason, which is what the panel greets with.
 *
 * <p>The panel can be open while the engine calculates - generating opens it - and what it read then
 * is "still calculating". Nothing about the suggestions query changes when the calculation lands, so
 * it is read again on every change of the run's state rather than served from the cache.
 */
const useAutomaticRosteringSuggestions = ({
  organizationalUnitCode,
  startDate,
  endDate,
  enabled,
  runState
}: UseArQuestionSuggestionsProps) => {
  const canAsk = enabled && !!organizationalUnitCode;
  const readForRunStateRef = useRef(runState);

  const { data, isFetching, refetch } =
    queries.useGetAutomaticRosteringQuestionSuggestions(
      organizationalUnitCode ?? '',
      startDate,
      endDate,
      { enabled: canAsk, retry: false }
    );

  useEffect(() => {
    if (readForRunStateRef.current === runState) return;

    readForRunStateRef.current = runState;
    if (canAsk) refetch();
  }, [runState, canAsk, refetch]);

  return {
    suggestions: data?.suggestions ?? [],
    isAvailable: data?.available ?? false,
    unavailableReason: data?.reason as
      | AutomaticRosteringUnavailableReason
      | undefined,
    isLoading: isFetching
  };
};

export default useAutomaticRosteringSuggestions;
